import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Message, Modal } from '@bedrock/components';
import RechargeDrawer, { rechargeFooterButtonProps } from './RechargeDrawer';
import CenterLoading from '#components/CenterLoading';
import VerticalLineTitle from '#components/VerticalLineTitle';
import { Z_INDEX } from '#utils/zIndex';
import withFunctionCall from '@mpc/common/src/utils/withFunctionCall';
import { useDemandOptions } from '#features/useDemandOptions';
import RechargeFormModel, {
  RECHARGE_FIELD_COLS,
} from '../RechargeFormModel';
import { useRechargeDraft } from '../hooks/useRechargeDraft';
import { useRechargeCalc } from '../hooks/useRechargeCalc';
import { submitRechargeOrder, confirmRecharge, abandonRecharge, buildReasonFields } from '../rechargeService';
import {
  FIELD_KEYS,
  FILTER_OPTION_KEY_RECHARGE_OPTIMIZER,
  RECHARGE_STATUS_DESC,
} from '../constants';
import type { RechargeFormContext, RechargeRenderData } from '../types';
import type {
  RechargeSubmitCmd,
  RechargeInitiatorRoleEnum,
  RechargeEntryEnum,
  CurrencyEnum,
  RechargeOrderDetailVo,
  RechargeStateEnum,
  RechargeDetailDto,
} from '#services/api';

/** filter_options 返回的优化师原始项（后端 UserOption：{ name, value }，对齐退款 useOptimizerOptions OptimizerRaw） */
type OptimizerRaw = {
  name?: string;
  value?: number;
};

/** 后端 { name, value } → select 选项（value=user_id，name=展示名；对齐退款 useRefundOptimizerOptions toOptimizerOptions） */
function toOptimizerOptions(
  list?: OptimizerRaw[]
): Array<{ value: number; name: string }> {
  return (list || []).map((o) => ({
    value: Number(o?.value),
    name: o?.name ?? '',
  }));
}

export interface RechargeFormViewProps {
  visible: boolean;
  /**
   * 关闭回调（对齐退款 FormDrawerView 的 onCancel；withFunctionCall 会 Omit 'onCancel' 并默认接管为 destroy）。
   * 受控用法传此回调；命令式 show() 不传（运行时 withFunctionCall 默认关抽屉）。
   */
  onCancel: () => void;
  onOk?: () => void;
  l2_demand_id: number;
  l3_demand_id?: number;
  recharge_order_id?: number;
  initiator_role?: RechargeInitiatorRoleEnum;
  entry: RechargeEntryEnum;
  overseas?: boolean;
  /** 外包端（供应商）标记，影响 useDemandOptions 选 filter_options 接口 */
  isExternal?: boolean;
  /**
   * 命令式发起（方案A）：调用方 show 前已调 fetchRechargeDraft 拿到草稿，透传覆盖组件内部拉取，避免草稿调两次。
   * 传了 detail 时 useRechargeDraft 跳过网络，仅做预填/calc 映射。
   */
  detail?: RechargeDetailDto | RechargeOrderDetailVo;
}

/**
 * 充值发起 Drawer（内/外包复用）。
 * 形态对齐退款 CreateRefundModal：普通 Drawer（80% 宽 min 960、右侧滑出、底部取消/提交操作栏），
 * 表单用 SForm FormModel renderType="descriptions"（只读字段 view + showComponent 展示，对齐退款 FormModel）。
 * - 加载草稿（draft）预填 + 核算预判（is_early_recharge）
 * - 填金额实时调 calc-preview 控制超充原因框显隐
 * - 提交组装 RechargeSubmitCmd → POST /order
 * 字段显隐/必填由 formConfig 按 角色×入口×海外×核算 动态控制（供应商端精简）。
 */
export const RechargeFormView: React.FC<RechargeFormViewProps> = (props) => {
  const {
    visible,
    onCancel,
    onOk,
    l2_demand_id,
    l3_demand_id,
    recharge_order_id,
    initiator_role,
    entry,
    overseas,
    isExternal,
    detail: externalDetail,
  } = props;
  const [form] = Form.useForm();
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [btnLoading, setBtnLoading] = useState(false);

  // 真实发起角色/海外标记：初始用 props 兜底，draft 返回后用后端 inferRechargeRole 推断值覆盖
  const [resolvedRole, setResolvedRole] = useState<
    RechargeInitiatorRoleEnum | undefined
  >(initiator_role);
  const [resolvedOverseas, setResolvedOverseas] = useState<boolean | undefined>(
    overseas
  );

  const { calc, setCalc, previewAmount, refreshWithEarly } = useRechargeCalc({
    l2_demand_id,
    l3_demand_id,
    recharge_order_id,
  });

  const { detail, loading } = useRechargeDraft(
    form,
    { l2_demand_id, l3_demand_id, recharge_order_id, entry },
    visible,
    ({ is_over_charge, is_early_recharge }) =>
      setCalc((prev) => ({
        ...prev,
        // draft/detail 拿到 is_over_charge/is_early_recharge 后同步初始化 need 标识：
        // 超充/提前=true 即需填原因（formConfig 显示条件 need===true 且 initiator_role===PLANNER，
        // 非策划发起由后端忽略原因字段）；首次发起金额 onBlur 的 calc-preview 会重算覆盖
        is_over_charge,
        need_over_charge_reason: is_over_charge,
        is_early_recharge,
        need_early_recharge_reason: is_early_recharge,
      })),
    (msg) => {
      // 草稿/详情失败：弹错误提示 + 关闭抽屉（原静默 onClose 吞掉错误信息，用户无感知，bug 39997）
      Message.error(msg || '当前单据不可发起充值');
      onCancel();
    },
    externalDetail
  );

  // calc-preview 实时算的 is_over_charge/is_early_recharge 同步到 form，
  // 让"是否超充/是否提前充值"显示实时值（draft 初始由 buildValues 带入；金额变化后 calc-preview 刷新覆盖）
  useEffect(() => {
    if (calc.is_over_charge !== undefined) {
      form.setFieldsValue({ [FIELD_KEYS.IS_OVER_CHARGE]: calc.is_over_charge });
    }
  }, [calc.is_over_charge]);
  useEffect(() => {
    if (calc.is_early_recharge !== undefined) {
      form.setFieldsValue({ [FIELD_KEYS.IS_EARLY_RECHARGE]: calc.is_early_recharge });
    }
  }, [calc.is_early_recharge]);

  // 优化师下拉选项：按二级单 id 反查需求单产品，后端 OptimizerUserOption 按 demand.productId
  // 反查 optimizer_config 配置的启用优化师（对齐退款 useRefundOptimizerOptions 的 demand_id 链路）
  const { optionsListMap } = useDemandOptions({
    keys: [FILTER_OPTION_KEY_RECHARGE_OPTIMIZER],
    params: { demand_id: l2_demand_id },
  });

  const optimizerOptions = useMemo(
    () =>
      toOptimizerOptions(
        optionsListMap?.[FILTER_OPTION_KEY_RECHARGE_OPTIMIZER] as
          | OptimizerRaw[]
          | undefined
      ),
    [optionsListMap]
  );

  // 填金额实时预判超充
  const handleValuesChange = (changed: Record<string, unknown>) => {
    if (Object.prototype.hasOwnProperty.call(changed, FIELD_KEYS.ORDER_AMOUNT)) {
      previewAmount(changed[FIELD_KEYS.ORDER_AMOUNT] as number | undefined);
    }
  };

  // draft 返回后，把后端 inferRechargeRole 推断的真实 role/overseas 同步到 state，
  // 供 calc-preview / submit 使用（这两接口信任前端传入的 initiator_role，不重新推断）
  useEffect(() => {
    if (detail?.initiator_role) setResolvedRole(detail.initiator_role);
    if (detail?.overseas !== undefined) setResolvedOverseas(detail.overseas);
  }, [detail]);

  // ctx 优先用 draft 返回的 initiatorRole/overseas（后端 inferRechargeRole 推断）
  const ctx: RechargeFormContext = {
    initiator_role: resolvedRole ?? 'PLANNER',
    entry,
    overseas: resolvedOverseas ?? false,
    isExternal,
  };

  const renderData: RechargeRenderData = {
    detailData: detail as RechargeRenderData['detailData'],
    formData,
    optionMap: { optimizer: optimizerOptions },
    ctx,
    calc,
  };

  // 退回来源节点（resubmit 态）：服务端从 journal 表反查退回前确认节点，横幅「XXXX 退回」展示
  const rejectFromState = recharge_order_id != null
    ? (detail as RechargeOrderDetailVo | undefined)?.reject_from_state
    : undefined;
  const rejectFromLabel = rejectFromState
    ? (RECHARGE_STATUS_DESC[rejectFromState as RechargeStateEnum] ?? rejectFromState)
    : '';

  const handleSubmit = async () => {
    try {
      setBtnLoading(true);
      const values = await form.validateFields();
      const overReason = buildReasonFields(
        values[FIELD_KEYS.OVER_CHARGE_REASON] as Parameters<
          typeof buildReasonFields
        >[0]
      );
      const earlyReason = buildReasonFields(
        values[FIELD_KEYS.EARLY_RECHARGE_REASON] as Parameters<
          typeof buildReasonFields
        >[0]
      );
      // 待发起态重新发起（退回后）：走 confirm 接口（可编辑字段 + 重新核算，action=CONFIRM）
      if (recharge_order_id != null) {
        const confirmCmd = {
          recharge_order_id,
          action: 'CONFIRM' as const,
          amount: values[FIELD_KEYS.ORDER_AMOUNT] as number,
          account_id_name: values[FIELD_KEYS.ACCOUNT_ID_NAME] as string,
          channel: values[FIELD_KEYS.CHANNEL] as string,
          platform: values[FIELD_KEYS.PLATFORM] as string,
          optimizer_ids: values[FIELD_KEYS.OPTIMIZER_IDS] as number[],
          over_charge_reason: calc.need_over_charge_reason
            ? overReason.reason
            : undefined,
          over_charge_attachment_ids: calc.need_over_charge_reason
            ? overReason.attachment_ids
            : undefined,
          early_recharge_reason: calc.need_early_recharge_reason
            ? earlyReason.reason
            : undefined,
          early_recharge_attachment_ids: calc.need_early_recharge_reason
            ? earlyReason.attachment_ids
            : undefined,
        };
        const res = await confirmRecharge(confirmCmd, { silent: true });
        if (res?.code === 1) {
          Message.success('提交成功');
          onOk?.();
        } else {
          refreshWithEarly(values[FIELD_KEYS.ORDER_AMOUNT] as number | undefined);
          Message.error(res?.message || '提交失败');
        }
        return;
      }
      const cmd: RechargeSubmitCmd = {
        l2_demand_id,
        l3_demand_id,
        recharge_order_id,
        entry,
        amount: values[FIELD_KEYS.ORDER_AMOUNT] as number,
        // currencyType 投影：币种只读（selectDisabled），从 form 值取回随单提交
        currency: values.currency as CurrencyEnum | undefined,
        account_id_name: values[FIELD_KEYS.ACCOUNT_ID_NAME] as string,
        channel: values[FIELD_KEYS.CHANNEL] as string,
        platform: values[FIELD_KEYS.PLATFORM] as string,
        optimizer_ids: values[FIELD_KEYS.OPTIMIZER_IDS] as number[],
        // 仅策划发起超充/提前时填原因；优化师/供应商发起留策划确认节点填
        over_charge_reason: calc.need_over_charge_reason
          ? overReason.reason
          : undefined,
        over_charge_attachment_ids: calc.need_over_charge_reason
          ? overReason.attachment_ids
          : undefined,
        early_recharge_reason: calc.need_early_recharge_reason
          ? earlyReason.reason
          : undefined,
        early_recharge_attachment_ids: calc.need_early_recharge_reason
          ? earlyReason.attachment_ids
          : undefined,
      };
      const res = await submitRechargeOrder(cmd, { silent: true });
      if (res?.code === 1) {
        Message.success('提交成功');
        onOk?.();
      } else {
        // 提交报错（如 TOCTOU 重算与前端预判不一致：后端判超充/提前但前端未显示原因框）
        // → 立即重算（含提前充值）刷新 need 标识，让新变为超充/提前的行显示原因框，用户补填后重提
        refreshWithEarly(values[FIELD_KEYS.ORDER_AMOUNT] as number | undefined);
        Message.error(res?.message || '提交失败');
      }
    } catch (err: unknown) {
      // validateFields reject（表单校验错误）：不弹提示，由字段自身展示
      const hasErrorFields =
        !!err &&
        typeof err === 'object' &&
        'errorFields' in err &&
        Array.isArray((err as { errorFields: unknown[] }).errorFields) &&
        (err as { errorFields: unknown[] }).errorFields.length > 0;
      if (!hasErrorFields) {
        // 业务错误（axios 拦截器对 code!==1 抛 ServiceError，如 TOCTOU 后端重算超充/提前
        // 但前端未显示原因框、或原因未填）→ 立即重算（含提前充值）刷新 need 标识，让新变为超充/提前的行显示原因框
        refreshWithEarly(
          form.getFieldValue(FIELD_KEYS.ORDER_AMOUNT) as number | undefined
        );
        Message.error((err as { message?: string })?.message || '提交失败');
      }
    } finally {
      setBtnLoading(false);
    }
  };

  // 待发起态重新发起：详情 VO 返回 permit_action.can_abandon，控制废弃按钮显隐（仅 resubmit 态有）
  const canAbandon =
    recharge_order_id != null &&
    (detail as RechargeOrderDetailVo | undefined)?.permit_action?.can_abandon ===
      true;

  // 废弃（仅待发起态，与详情确认页 handleAbandon 一致）
  const handleAbandon = () => {
    Modal.confirm({
      title: '确认废弃该充值单？',
      content: '废弃后该充值单作废，不可恢复。',
      // 高于 Drawer(zIndex 1030)，避免被抽屉盖住（rock-modal 默认 1000 < drawer）
      zIndex: Z_INDEX.modal,
      onOk: async () => {
        try {
          const res = await abandonRecharge(
            { order_id: recharge_order_id as number },
            { silent: true }
          );
          if (res?.code === 1) {
            Message.success('已废弃');
            onOk?.();
          } else {
            Message.error(res?.message || '废弃失败');
          }
        } catch (err: unknown) {
          Message.error((err as { message?: string })?.message || '废弃失败');
        }
      },
    });
  };

  return (
    <RechargeDrawer
      // 预算看板（BUDGET_DASHBOARD）入口：Drawer 嵌在预算看板 Drawer 内，须 getContainer={false}
      // 挂载到父容器（对齐退款 FormDrawer），否则 portal 到 body 会被看板 mask/层级遮挡导致不可见；
      // 报价信息（QUOTE_INFO）入口：需求详情页 inline 弹出，仍 portal 到 body（不设 getContainer）。
      getContainer={entry === 'BUDGET_DASHBOARD' ? false : undefined}
      visible={visible && !!detail}
      onClose={onCancel}
      maskClosable={false}
      title={recharge_order_id != null ? '修改充值单' : '发起充值单'}
      footer={
        <>
          <Button {...rechargeFooterButtonProps} type="checked-neutral" onClick={onCancel}>
            取消
          </Button>
          {canAbandon && (
            <Button {...rechargeFooterButtonProps} danger onClick={handleAbandon} loading={btnLoading}>
              废弃
            </Button>
          )}
          <Button {...rechargeFooterButtonProps} type="primary" onClick={handleSubmit} loading={btnLoading}>
            提交
          </Button>
        </>
      }
    >
      {loading ? (
        <CenterLoading />
      ) : (
        <Form
          form={form}
          layout="vertical"
          onValuesChange={(changed) => {
            setFormData(form.getFieldsValue());
            handleValuesChange(changed);
          }}
        >
          <VerticalLineTitle text="充值信息" style={{ marginBottom: 16 }} />
          {recharge_order_id != null &&
            (detail as RechargeOrderDetailVo | undefined)?.reject_reason && (
              <Alert
                message={rejectFromLabel ? `${rejectFromLabel} 退回` : '已退回'}
                description={`退回原因：${(detail as RechargeOrderDetailVo).reject_reason}`}
                type="warning"
                showIcon
                className="mb-4"
              />
            )}
          <RechargeFormModel
            renderType="descriptions"
            renderData={renderData}
            itemCols={[...RECHARGE_FIELD_COLS]}
            form={form}
          />
        </Form>
      )}
    </RechargeDrawer>
  );
};

// 命令式导出（对齐退款 FormDrawer:467）：RechargeForm.show({...}) 挂载到 body。
// 保留具名裸组件 RechargeFormView 供 resubmit（受控）/外包壳/demo 使用。
export default withFunctionCall(RechargeFormView);
